import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import './IntegrationsPage.css';

type IntegrationStatus = 'available' | 'coming-soon';
type CategoryKind = 'case' | 'document' | 'insurance';

interface Integration {
  name: string;
  descKey: string;
  status: IntegrationStatus;
}

interface IntegrationCategory {
  kind: CategoryKind;
  items: Integration[];
}

const categories: IntegrationCategory[] = [
  {
    kind: 'case',
    items: [
      { name: 'Clio', descKey: 'integrations.items.clio', status: 'available' },
      { name: 'Litify', descKey: 'integrations.items.litify', status: 'available' },
      { name: 'FileVine', descKey: 'integrations.items.filevine', status: 'available' },
      { name: 'Needles (Neos)', descKey: 'integrations.items.needles', status: 'available' },
      { name: 'SmartAdvocate', descKey: 'integrations.items.smartadvocate', status: 'coming-soon' },
      { name: 'TrialWorks', descKey: 'integrations.items.trialworks', status: 'coming-soon' },
    ],
  },
  {
    kind: 'document',
    items: [
      { name: 'Relativity', descKey: 'integrations.items.relativity', status: 'available' },
      { name: 'NetDocuments', descKey: 'integrations.items.netdocuments', status: 'available' },
      { name: 'iManage', descKey: 'integrations.items.imanage', status: 'available' },
      { name: 'Logikcull', descKey: 'integrations.items.logikcull', status: 'coming-soon' },
    ],
  },
  {
    kind: 'insurance',
    items: [
      { name: 'Guidewire ClaimCenter', descKey: 'integrations.items.guidewire', status: 'available' },
      { name: 'Duck Creek Claims', descKey: 'integrations.items.duckcreek', status: 'available' },
      { name: 'Origami Risk', descKey: 'integrations.items.origami', status: 'coming-soon' },
    ],
  },
];

const exposedTools = [
  { name: 'run_study', descKey: 'integrations.mcp.tools.runStudy' },
  { name: 'query_participants', descKey: 'integrations.mcp.tools.queryParticipants' },
  { name: 'get_findings', descKey: 'integrations.mcp.tools.getFindings' },
  { name: 'get_recommendations', descKey: 'integrations.mcp.tools.getRecommendations' },
  { name: 'upload_documents', descKey: 'integrations.mcp.tools.uploadDocuments' },
  { name: 'export_report', descKey: 'integrations.mcp.tools.exportReport' },
];

const agentPermissionKeys = [
  'integrations.mcp.permissions.read',
  'integrations.mcp.permissions.createStudies',
  'integrations.mcp.permissions.uploadDocs',
  'integrations.mcp.permissions.exportReports',
  'integrations.mcp.permissions.modifyLifecycle',
];

const IntegrationsPage: React.FC = () => {
  const { t } = useTranslation();
  usePageTitle('Integrations - Connect JuryView');
  const [keyRevealed, setKeyRevealed] = useState(false);
  const [permissions, setPermissions] = useState<boolean[]>(() =>
    agentPermissionKeys.map((_, i) => i !== 2 && i !== 4)
  );
  const heroRef = useRef<HTMLElement>(null);

  const togglePermission = (idx: number) => {
    setPermissions((prev) => prev.map((v, i) => (i === idx ? !v : v)));
  };

  const scrollToHero = () => {
    heroRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="integrations-page">
      {/* ===== Hero ===== */}
      <section className="integrations-hero" id="top" ref={heroRef}>
        <div className="container">
          <div className="hero-eyebrow" aria-hidden="true">
            <span className="eyebrow-rule" />
            <span className="eyebrow-mark">✦</span>
            <span className="eyebrow-rule" />
          </div>
          <span className="hero-label">{t('integrations.label')}</span>
          <h1>{t('integrations.title')}</h1>
          <p className="hero-subtitle">{t('integrations.subtitle')}</p>
          <div className="hero-actions">
            <Link to="/contact" className="hero-cta">
              {t('integrations.requestDocumentation')}
            </Link>
          </div>
        </div>
      </section>

      {/* ===== Integration categories ===== */}
      <section className="integration-categories">
        <div className="container">
          {categories.map((category) => (
            <div key={category.kind} className={`category-block kind-${category.kind}`}>
              <div className="category-header">
                <h2>{t(`integrations.categories.${category.kind}.title`)}</h2>
                <p>{t(`integrations.categories.${category.kind}.description`)}</p>
              </div>
              <div className="integration-grid">
                {category.items.map((item) => (
                  <div key={item.name} className={`integration-card ${item.status}`}>
                    <span className="card-rail" aria-hidden="true" />
                    <div className="card-body">
                      <h3>{item.name}</h3>
                      <p>{t(item.descKey)}</p>
                    </div>
                    {item.status === 'available' ? (
                      <button type="button" className="card-action" onClick={scrollToHero}>
                        {t('integrations.connect')}
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <path d="M3 7H11M11 7L7 3M11 7L7 11" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    ) : (
                      <span className="card-soon">{t('integrations.comingSoon')}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          <p className="trademark-notice">{t('integrations.trademarkNotice')}</p>
        </div>
      </section>

      {/* ===== API Access ===== */}
      <section className="api-section">
        <div className="container">
          <div className="api-card">
            <div className="api-header">
              <div>
                <span className="api-eyebrow">{t('integrations.api.eyebrow')}</span>
                <h2>{t('integrations.api.title')}</h2>
                <p>{t('integrations.api.description')}</p>
              </div>
              <div className="api-status">
                <span className="status-dot" />
                {t('integrations.api.live')}
              </div>
            </div>

            <div className="api-fields">
              <div className="api-field">
                <label>{t('integrations.api.apiKey')}</label>
                <div className="field-row">
                  <code className="field-value">
                    {keyRevealed ? 'jv_demo_0000aaaa1111bbbb2222cccc' : 'jv_demo_••••••••••••••••'}
                  </code>
                  <button type="button" className="field-action" onClick={() => setKeyRevealed((v) => !v)}>
                    {keyRevealed ? t('integrations.api.hide') : t('integrations.api.reveal')}
                  </button>
                </div>
              </div>

              <div className="api-field">
                <label>{t('integrations.api.webhookUrl')}</label>
                <div className="field-row">
                  <code className="field-value muted">{t('integrations.api.notConfigured')}</code>
                  <button type="button" className="field-action" onClick={scrollToHero}>
                    {t('integrations.api.configure')}
                  </button>
                </div>
              </div>
            </div>

            <div className="api-footer">
              <button type="button" className="api-link" onClick={scrollToHero}>{t('integrations.api.documentation')}</button>
              <span className="api-link-divider" aria-hidden="true">|</span>
              <button type="button" className="api-link" onClick={scrollToHero}>{t('integrations.api.webhookEvents')}</button>
              <span className="api-link-divider" aria-hidden="true">|</span>
              <button type="button" className="api-link" onClick={scrollToHero}>{t('integrations.api.rateLimits')}</button>
            </div>
          </div>
        </div>
      </section>

      {/* ===== MCP Server ===== */}
      <section className="mcp-section">
        <div className="container">
          <div className="mcp-card">
            <div className="mcp-header">
              <div>
                <span className="mcp-eyebrow">{t('integrations.mcp.eyebrow')}</span>
                <h2>{t('integrations.mcp.title')}</h2>
                <p>{t('integrations.mcp.description')}</p>
              </div>
              <div className="mcp-status">
                <span className="status-dot" />
                {t('integrations.mcp.active')}
              </div>
            </div>

            <div className="api-fields">
              <div className="api-field">
                <label>{t('integrations.mcp.endpoint')}</label>
                <div className="field-row">
                  <code className="field-value">mcp://juryview.com/v1</code>
                  <button type="button" className="field-action" onClick={scrollToHero}>
                    {t('integrations.mcp.copy')}
                  </button>
                </div>
              </div>

              <div className="api-field">
                <label>{t('integrations.mcp.authToken')}</label>
                <div className="field-row">
                  <code className="field-value">jv_demo_tk_••••••••••••</code>
                  <button type="button" className="field-action" onClick={scrollToHero}>
                    {t('integrations.mcp.regenerate')}
                  </button>
                </div>
              </div>
            </div>

            <div className="mcp-block">
              <h4>{t('integrations.mcp.exposedTools')}</h4>
              <div className="tools-grid">
                {exposedTools.map((tool) => (
                  <div key={tool.name} className="tool-row">
                    <code className="tool-name">{tool.name}</code>
                    <span className="tool-desc">{t(tool.descKey)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mcp-block">
              <h4>{t('integrations.mcp.agentPermissions')}</h4>
              <div className="permissions-list">
                {agentPermissionKeys.map((permKey, i) => (
                  <label key={permKey} className="permission-row">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={permissions[i] ?? false}
                      className={`toggle ${permissions[i] ? 'on' : 'off'}`}
                      onClick={() => togglePermission(i)}
                    >
                      <span className="toggle-thumb" />
                    </button>
                    <span className="permission-label">{t(permKey)}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="api-footer">
              <button type="button" className="api-link" onClick={scrollToHero}>{t('integrations.mcp.setupGuide')}</button>
              <span className="api-link-divider" aria-hidden="true">|</span>
              <button type="button" className="api-link" onClick={scrollToHero}>{t('integrations.mcp.agentSdk')}</button>
              <span className="api-link-divider" aria-hidden="true">|</span>
              <button type="button" className="api-link" onClick={scrollToHero}>{t('integrations.mcp.toolReference')}</button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IntegrationsPage;
